import { useEffect, useState } from "react";
import { Download, Share2, X } from "lucide-react";
import { useInstallPWA } from "../lib/useInstallPWA";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";

/**
 * Card discreto para convidar o usuário a instalar o PWA.
 * - Aparece apenas dentro do ambiente do tenant (o chamador decide onde renderizar).
 * - Respeita instalação existente (não aparece se já em display-mode standalone).
 * - Persiste dispensa por-slug em localStorage (não volta a incomodar).
 * - iOS/Safari: abre modal com passos "Adicionar à Tela de Início".
 */
export default function InstallPWACard({ slug, tenantName }) {
  const { canInstall, canPrompt, isIos, prompt } = useInstallPWA();
  const dismissKey = `pwa-dismissed-${slug || "app"}`;
  const [dismissed, setDismissed] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);

  useEffect(() => {
    try {
      setDismissed(window.localStorage.getItem(dismissKey) === "1");
    } catch { /* localStorage bloqueado — mostra card */ }
  }, [dismissKey]);

  if (!canInstall || dismissed) return null;

  const handleDismiss = () => {
    try { window.localStorage.setItem(dismissKey, "1"); } catch {}
    setDismissed(true);
  };

  const handleInstall = () => {
    if (canPrompt) prompt();
    else setShowIosHelp(true);
  };

  return (
    <>
      <div
        data-testid="install-pwa-card"
        className="mt-6 rounded-2xl border border-brand-100 bg-white/90 p-4 sm:p-5 shadow-sm shadow-brand-500/5"
      >
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-500 text-white">
            <Download size={18} />
          </div>
          <div className="flex-1">
            <div className="font-display text-base font-semibold leading-tight">Instalar FILA Gestão</div>
            <p className="mt-1 text-xs text-zinc-600 leading-relaxed">
              Tenha acesso mais rápido ao seu ambiente {tenantName ? <span className="font-medium text-zinc-800">{tenantName}</span> : null} neste dispositivo.
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <button
                data-testid="install-pwa-card-install"
                onClick={handleInstall}
                className="inline-flex items-center gap-1.5 rounded-full bg-brand-500 px-4 py-2 text-xs font-semibold uppercase tracking-widest text-white hover:bg-brand-600 transition-all"
              >
                {isIos && !canPrompt ? (<><Share2 size={13} /> Como instalar</>) : (<><Download size={13} /> Instalar</>)}
              </button>
              <button
                data-testid="install-pwa-card-dismiss"
                onClick={handleDismiss}
                className="inline-flex items-center gap-1 rounded-full border border-zinc-200 bg-white px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-600 hover:border-zinc-300 hover:text-zinc-800"
              >
                Agora não
              </button>
            </div>
          </div>
          <button
            aria-label="Fechar"
            onClick={handleDismiss}
            className="rounded-full p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600"
            data-testid="install-pwa-card-close"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      <Dialog open={showIosHelp} onOpenChange={setShowIosHelp}>
        <DialogContent data-testid="dialog-ios-install-login">
          <DialogHeader>
            <DialogTitle className="font-display">Instalar no iPhone / iPad</DialogTitle>
            <DialogDescription>
              O Safari não tem instalação automática — siga estes passos:
            </DialogDescription>
          </DialogHeader>
          <ol className="space-y-2 text-sm text-gray-700">
            <li>1. Certifique-se de estar no <strong>Safari</strong> (não use Chrome no iOS).</li>
            <li>2. Toque no botão <strong>Compartilhar</strong> (quadradinho com seta para cima).</li>
            <li>3. Role e toque em <strong>“Adicionar à Tela de Início”</strong>.</li>
            <li>4. Confirme o nome e toque em <strong>Adicionar</strong>.</li>
            <li>5. Abra pelo ícone salvo — ele já vai abrir direto no seu ambiente.</li>
          </ol>
        </DialogContent>
      </Dialog>
    </>
  );
}
